import React, { useState, useEffect } from 'react';
import { Users, UserCheck, X, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import { db } from '../../firebase/config';
import { collection, getDocs, doc, updateDoc, query, where, writeBatch } from 'firebase/firestore';

const BatchAssignmentsWidget = () => {
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [newBatchName, setNewBatchName] = useState('');
  const [editBatchName, setEditBatchName] = useState('');
  const [selectedMentor, setSelectedMentor] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [studentsSnap, mentorsSnap] = await Promise.all([
        getDocs(collection(db, 'students')),
        getDocs(query(collection(db, 'users'), where('role', '==', 'mentor')))
      ]);

      const studentsList = studentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudents(studentsList.sort((a, b) => a.rollNumber.localeCompare(b.rollNumber)));

      const mentorsList = mentorsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMentors(mentorsList);

      const batchMap = {};
      studentsList.forEach(student => {
        // FIXED: Skip students without a valid batch assignment
        if (student.batch && student.batch !== null && student.batch !== '') {
          if (!batchMap[student.batch]) {
            batchMap[student.batch] = { name: student.batch, students: [], mentor: null };
          }
          batchMap[student.batch].students.push(student);
        }
      });

      mentorsList.forEach(mentor => {
        if (mentor.assignedBatch && batchMap[mentor.assignedBatch]) {
          batchMap[mentor.assignedBatch].mentor = mentor;
        }
      });

      setBatches(Object.values(batchMap).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSelectRange = () => {
    if (!rangeStart || !rangeEnd) {
      alert('Enter both start and end roll numbers');
      return;
    }

    const inRange = students.filter(s => 
      s.rollNumber >= rangeStart.toUpperCase() && s.rollNumber <= rangeEnd.toUpperCase()
    ).map(s => s.id);

    setSelectedStudents(prev => [...new Set([...prev, ...inRange])]);
    setRangeStart('');
    setRangeEnd('');
  };

  const handleCreateBatch = async () => {
    if (!newBatchName || selectedStudents.length === 0) {
      alert('Enter batch name and select students');
      return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(db);
      selectedStudents.forEach(studentId => {
        batch.update(doc(db, 'students', studentId), { batch: newBatchName });
      });
      await batch.commit();

      alert(`Batch ${newBatchName} created with ${selectedStudents.length} students`);
      setShowCreateModal(false);
      setNewBatchName('');
      setSelectedStudents([]);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to create batch');
    }
    setLoading(false);
  };

  const openEditModal = (batch) => {
    setSelectedBatch(batch);
    setEditBatchName(batch.name);
    setSelectedStudents(batch.students.map(s => s.id));
    setShowEditModal(true);
  };

  const handleEditBatch = async () => {
    if (!editBatchName) {
      alert('Enter batch name');
      return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      // Get the mentor assigned to this batch (if any)
      const batchMentor = mentors.find(m => m.assignedBatch === selectedBatch.name);
      
      // Remove old students from batch and clear their mentor info
      selectedBatch.students.forEach(student => {
        if (!selectedStudents.includes(student.id)) {
          batch.update(doc(db, 'students', student.id), { 
            batch: null,
            mentorName: null,
            mentorEmail: null 
          });
        }
      });

      // Update selected students with batch name
      selectedStudents.forEach(studentId => {
        const updateData = { batch: editBatchName };
        
        // If batch has a mentor, add mentor info to new students
        if (batchMentor) {
          updateData.mentorName = batchMentor.name;
          updateData.mentorEmail = batchMentor.email;
        }
        
        batch.update(doc(db, 'students', studentId), updateData);
      });

      await batch.commit();

      alert('Batch updated successfully');
      setShowEditModal(false);
      setSelectedStudents([]);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to update batch');
    }
    setLoading(false);
  };

  const handleDeleteBatch = async (batch) => {
    if (!window.confirm(`Delete batch ${batch.name}? All students will be unassigned.`)) return;

    setLoading(true);
    try {
      const batchOp = writeBatch(db);

      // Unassign all students and clear mentor information
      batch.students.forEach(student => {
        batchOp.update(doc(db, 'students', student.id), { 
          batch: null,
          mentorName: null,
          mentorEmail: null 
        });
      });

      // Unassign mentor
      if (batch.mentor) {
        batchOp.update(doc(db, 'users', batch.mentor.id), { assignedBatch: null });
      }

      await batchOp.commit();
      alert('Batch deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete batch');
    }
    setLoading(false);
  };

  const handleAssignMentor = async () => {
    if (!selectedMentor) {
      alert('Select a mentor');
      return;
    }

    setLoading(true);
    try {
      const batchOp = writeBatch(db);
      const selectedMentorData = mentors.find(m => m.id === selectedMentor);
      
      // Unassign old mentor if exists
      const oldMentor = mentors.find(m => m.assignedBatch === selectedBatch.name);
      if (oldMentor) {
        batchOp.update(doc(db, 'users', oldMentor.id), { assignedBatch: null });
        
        // Remove mentorName and mentorEmail from students of old mentor
        selectedBatch.students.forEach(student => {
          batchOp.update(doc(db, 'students', student.id), { 
            mentorName: null,
            mentorEmail: null 
          });
        });
      }

      // Assign new mentor to users collection
      batchOp.update(doc(db, 'users', selectedMentor), { 
        assignedBatch: selectedBatch.name 
      });

      // Update all students in the batch with mentor information
      selectedBatch.students.forEach(student => {
        batchOp.update(doc(db, 'students', student.id), { 
          mentorName: selectedMentorData.name,
          mentorEmail: selectedMentorData.email 
        });
      });

      // Update or create batchAssignments document
      const batchAssignmentRef = doc(db, 'batchAssignments', selectedBatch.name);
      batchOp.set(batchAssignmentRef, {
        batch: selectedBatch.name,
        division: selectedBatch.students[0]?.division || 'B',
        mentorName: selectedMentorData.name,
        mentorEmail: selectedMentorData.email,
        updatedAt: new Date().toISOString(),
        updatedBy: selectedMentorData.email
      }, { merge: true });

      await batchOp.commit();

      alert('Mentor assigned successfully');
      setShowAssignModal(false);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to assign mentor');
    }
    setLoading(false);
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredStudents.map(s => s.id);
    setSelectedStudents(prev => [...new Set([...prev, ...allFilteredIds])]);
  };

  const handleUnselectAll = () => {
    setSelectedStudents([]);
  };

  const filteredStudents = students.filter(s =>
    s.rollNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${s.firstName} ${s.surname}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">{batches.length} batches</div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus className="w-4 h-4" />
            <span>Create Batch</span>
          </button>
        </div>

        <div className="space-y-2">
          {batches.map(batch => (
            <div key={batch.name} className="p-4 bg-gray-50 rounded-lg flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-semibold">Batch {batch.name}</p>
                  <p className="text-xs text-gray-600">{batch.students.length} students</p>
                  {batch.mentor ? (
                    <p className="text-xs text-green-600">✓ {batch.mentor.name}</p>
                  ) : (
                    <p className="text-xs text-red-600">⚠ No mentor</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => openEditModal(batch)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  title="Edit Batch"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setSelectedBatch(batch);
                    setSelectedMentor(batch.mentor?.id || '');
                    setShowAssignModal(true);
                  }}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  {batch.mentor ? 'Change' : 'Assign'}
                </button>
                <button
                  onClick={() => handleDeleteBatch(batch)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                  title="Delete Batch"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create/Edit Modal (shared logic) */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">{showEditModal ? 'Edit' : 'Create'} Batch</h3>
                <button onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedStudents([]);
                }}>
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Batch Name</label>
                <input
                  type="text"
                  value={showEditModal ? editBatchName : newBatchName}
                  onChange={(e) => showEditModal ? setEditBatchName(e.target.value.toUpperCase()) : setNewBatchName(e.target.value.toUpperCase())}
                  placeholder="e.g., FA, FB, FC"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium mb-2">Select by Range</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={rangeStart}
                    onChange={(e) => setRangeStart(e.target.value)}
                    placeholder="From (e.g., FB101)"
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    value={rangeEnd}
                    onChange={(e) => setRangeEnd(e.target.value)}
                    placeholder="To (e.g., FB120)"
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <button
                    onClick={handleSelectRange}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Select
                  </button>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search students..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>

              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {selectedStudents.length} students selected
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleUnselectAll}
                    className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Unselect All
                  </button>
                </div>
              </div>

              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {filteredStudents.map(student => (
                  <label
                    key={student.id}
                    className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => handleSelectStudent(student.id)}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-medium">{student.rollNumber}</p>
                      <p className="text-sm text-gray-600">{student.firstName} {student.surname}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white p-6 border-t flex space-x-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedStudents([]);
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={showEditModal ? handleEditBatch : handleCreateBatch}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : showEditModal ? 'Update Batch' : 'Create Batch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Mentor Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Assign Mentor - Batch {selectedBatch?.name}</h3>
              <button onClick={() => setShowAssignModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm">
              <p><strong>Students:</strong> {selectedBatch?.students.length}</p>
              {selectedBatch?.mentor && (
                <p><strong>Current:</strong> {selectedBatch.mentor.name}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Select Mentor</label>
              <select
                value={selectedMentor}
                onChange={(e) => setSelectedMentor(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">-- Select Mentor --</option>
                {mentors.map(mentor => (
                  <option key={mentor.id} value={mentor.id}>
                    {mentor.name} {mentor.assignedBatch && `(Batch ${mentor.assignedBatch})`}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignMentor}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BatchAssignmentsWidget;